<template>
  <div class="form-schema-renderer">
    <p
      v-if="formDescription"
      class="form-schema-renderer__desc text-300-default neutral_2"
      v-html="linkifyText(formDescription)"
    />
    <div
      v-for="field in definition.fields"
      :key="field.id"
      class="form-schema-renderer__field"
    >
      <template v-if="field.type === 'section'">
        <h3 class="heading-500 neutral_1 mt-15px mb-5px">{{ field.label }}</h3>
      </template>
      <template v-else-if="field.type === 'description'">
        <p
          v-if="descriptionFieldText(field)"
          class="form-schema-renderer__hint text-300-default neutral_2 mb-10px"
          v-html="linkifyText(descriptionFieldText(field))"
        />
      </template>
      <template v-else>
        <label class="text-400-medium neutral_1 d-block mb-5px">
          {{ field.label }}
          <span v-if="field.required" class="text-danger">*</span>
        </label>
        <p
          v-if="fieldHelpText(field)"
          class="text-200-default neutral_3 mb-5px"
          v-html="linkifyText(fieldHelpText(field))"
        />

        <textarea
          v-if="field.type === 'short_text'"
          v-model="localAnswers[field.id]"
          class="form-control input-custom w_100 form-schema-renderer__textarea form-schema-renderer__textarea--short"
          rows="2"
          :placeholder="field.placeholder || ''"
          :disabled="readonly"
        />

        <input
          v-else-if="field.type === 'email'"
          v-model="localAnswers[field.id]"
          class="form-control input-custom w_100"
          type="email"
          :placeholder="field.placeholder || ''"
          :disabled="readonly"
        />

        <textarea
          v-else-if="field.type === 'long_text'"
          v-model="localAnswers[field.id]"
          class="form-control input-custom w_100 form-schema-renderer__textarea form-schema-renderer__textarea--long"
          rows="6"
          :placeholder="field.placeholder || ''"
          :disabled="readonly"
        />

        <input
          v-else-if="field.type === 'number'"
          v-model.number="localAnswers[field.id]"
          class="form-control input-custom w_100"
          type="number"
          :placeholder="field.placeholder || ''"
          :disabled="readonly"
        />

        <input
          v-else-if="field.type === 'date'"
          v-model="localAnswers[field.id]"
          class="form-control input-custom w_100"
          type="date"
          :disabled="readonly"
        />

        <select
          v-else-if="field.type === 'dropdown'"
          v-model="localAnswers[field.id]"
          class="form-control input-custom w_100"
          :disabled="readonly"
        >
          <option value="">-- Chọn --</option>
          <option v-for="opt in field.options || []" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
        </select>

        <div v-else-if="field.type === 'single_choice'" class="form-choice-list">
          <label v-for="opt in field.options || []" :key="opt.id" class="form-choice-item">
            <input
              type="radio"
              :name="'field_' + field.id"
              :value="opt.id"
              v-model="localAnswers[field.id]"
              :disabled="readonly"
            />
            <span
              class="form-choice-item__label"
              v-html="linkifyText(opt.label)"
              @click="onChoiceLabelClick"
            />
          </label>
        </div>

        <div v-else-if="field.type === 'multi_choice'" class="form-choice-list">
          <label v-for="opt in field.options || []" :key="opt.id" class="form-choice-item">
            <input
              type="checkbox"
              :value="opt.id"
              v-model="multiChoice[field.id]"
              :disabled="readonly"
              @change="syncMulti(field.id)"
            />
            <span
              class="form-choice-item__label"
              v-html="linkifyText(opt.label)"
              @click="onChoiceLabelClick"
            />
          </label>
        </div>

        <div v-else-if="field.type === 'rating'" class="form-rating">
          <button
            v-for="n in (field.max || 5)"
            :key="n"
            type="button"
            class="form-rating__star"
            :class="{ active: Number(localAnswers[field.id]) >= n }"
            :disabled="readonly"
            @click="setRating(field.id, n)"
          >
            ★
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { linkifyText } from "../../helper/linkifyText";

export default {
  name: "FormSchemaRenderer",
  props: {
    definition: { type: Object, required: true },
    value: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      localAnswers: { ...(this.value || {}) },
      multiChoice: {},
    };
  },
  computed: {
    formDescription() {
      const text = String((this.definition && this.definition.description) || "").trim();
      return text || "";
    },
  },
  watch: {
    definition: {
      deep: true,
      handler() {
        this.initMultiChoice();
      },
    },
    value: {
      deep: true,
      handler(v) {
        const next = { ...(v || {}) };
        if (this.answersEqual(next, this.localAnswers)) return;
        this.localAnswers = next;
        this.initMultiChoice();
      },
    },
    localAnswers: {
      deep: true,
      handler(v) {
        const payload = { ...v };
        if (this.answersEqual(payload, this.value || {})) return;
        this.$emit("input", payload);
      },
    },
  },
  created() {
    this.initMultiChoice();
  },
  methods: {
    linkifyText,
    answersEqual(a, b) {
      return JSON.stringify(a || {}) === JSON.stringify(b || {});
    },
    descriptionFieldText(field) {
      return String((field && field.content) || "").trim();
    },
    fieldHelpText(field) {
      return String((field && field.description) || "").trim();
    },
    initMultiChoice() {
      const mc = {};
      ((this.definition && this.definition.fields) || []).forEach((field) => {
        if (field.type === "multi_choice") {
          mc[field.id] = Array.isArray(this.localAnswers[field.id])
            ? [...this.localAnswers[field.id]]
            : [];
        }
      });
      this.multiChoice = mc;
    },
    syncMulti(fieldId) {
      this.$set(this.localAnswers, fieldId, [...(this.multiChoice[fieldId] || [])]);
    },
    setRating(fieldId, n) {
      if (this.readonly) return;
      this.$set(this.localAnswers, fieldId, n);
    },
    /** Click vào link trong đáp án: mở tab mới, không chọn/bỏ chọn radio/checkbox. */
    onChoiceLabelClick(e) {
      const a = e && e.target && e.target.closest ? e.target.closest("a") : null;
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      const href = a.getAttribute("href");
      if (href) {
        window.open(href, "_blank", "noopener,noreferrer");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.form-schema-renderer__desc,
.form-schema-renderer__hint {
  white-space: pre-wrap;
  line-height: 1.5;
  ::v-deep a {
    color: #2563eb;
    text-decoration: underline;
    word-break: break-all;
  }
}
.form-schema-renderer__hint {
  margin: 0;
  padding: 0;
  font-style: italic;
}
.form-schema-renderer__field {
  margin-bottom: 14px;
}
.form-schema-renderer__textarea {
  resize: vertical;
  min-height: 0;
}
.form-schema-renderer__textarea--short {
  min-height: calc(1.5em * 2 + 16px);
}
.form-schema-renderer__textarea--long {
  min-height: calc(1.5em * 6 + 16px);
}
.form-choice-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.form-choice-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}
.form-choice-item__label {
  flex: 1;
  white-space: pre-wrap;
  line-height: 1.45;
  ::v-deep a {
    color: #2563eb;
    text-decoration: underline;
    word-break: break-all;
  }
}
.form-rating__star {
  border: none;
  background: transparent;
  font-size: 28px;
  color: #d1d5db;
  cursor: pointer;
  padding: 0 2px;
  &.active {
    color: #f59e0b;
  }
  &:disabled {
    cursor: default;
  }
}
.text-danger {
  color: #dc2626;
}
</style>
